import { io, Socket } from 'socket.io-client';
import { logger } from '#utils';
import getConfigVars from '#utils/get-config-vars';
import { useEffect, useState } from 'react';

const { NOTIFICATIONS_ENDPOINT } = getConfigVars();

export type Notification = {
  id: string;
  iri: string;
  oldStatus: string;
  newStatus: string;
  type: string;
};

interface ServerToClientEvents {
  livestream_status_update: (notification: Notification) => void;
}

let socket: Socket<ServerToClientEvents>;

const initializeSocket = () => {
  if (!socket) {
    logger.debug('Initializing socket');

    socket = io(NOTIFICATIONS_ENDPOINT, {
      path: '/notifications',
      transports: ['websocket'],
      reconnectionDelay: 5000,
      reconnectionDelayMax: 60000,
      withCredentials: true,
    });

    socket.on('connect', () => {
      const engine = socket.io.engine;
      logger.debug('WSS: Connected', {
        id: socket.id,
        connected: socket.connected,
        transport_name: engine.transport.name,
      });

      if (socket.recovered) {
        logger.info('WSS: Connection recovered');
      }
    });

    socket.io.on('reconnect', (attempt) => {
      logger.debug('WSS: Reconnected to server after', { attempt });
    });

    socket.on('disconnect', (reason, description) => {
      logger.debug('WSS: Disconnect', {
        reason,
        ...(description && { description }),
      });
    });

    socket.on('connect_error', (err) => {
      logger.error(`WSS: Connect error`, { err });
    });
  }

  return socket;
};

const useSocket = () => {
  const [connected, setConnected] = useState(socket?.connected);
  const [error, setError] = useState(null);

  useEffect(() => {
    const _socket = initializeSocket();

    _socket.on('connect', () => {
      setConnected(true);
    });

    _socket.on('disconnect', () => {
      setConnected(false);
      logger.debug('WSS: Disconnect');
    });

    _socket.on('connect_error', (err) => {
      setError(err);
      logger.error(`WSS: Connect error`, { err });
    });

    return () => {
      _socket.off('connect');
      _socket.off('disconnect');
      _socket.off('connect_error');
    };
  }, []);

  return { socket, connected, error };
};

export default useSocket;
